import React from "react";
import "./style.scss";
import { motion } from "framer-motion";

const industryConfig = {
  film: {
    gradient: "from-slate-900 via-purple-900 to-slate-800",
    accent: "from-purple-400 to-pink-400",
    tag: "Industry Solution",
    stats: [
      { value: "3×", label: "Faster Procurement" },
      { value: "40%", label: "Cost Reduction" },
      { value: "100%", label: "Supply Visibility" },
    ],
  },
  health: {
    gradient: "from-slate-900 via-blue-900 to-teal-900",
    accent: "from-blue-400 to-teal-400",
    tag: "Industry Solution",
    stats: [
      { value: "80%", label: "Process Automation" },
      { value: "25%", label: "Cost Savings" },
      { value: "99%", label: "Compliance Rate" },
    ],
  },
  hotel: {
    gradient: "from-slate-900 via-orange-900 to-slate-800",
    accent: "from-orange-400 to-amber-400",
    tag: "Industry Solution",
    stats: [
      { value: "50%", label: "Less Manual Work" },
      { value: "30%", label: "Cost Reduction" },
      { value: "2×", label: "Supplier Efficiency" },
    ],
  },
  electronics: {
    gradient: "from-slate-900 via-violet-900 to-indigo-900",
    accent: "from-violet-400 to-indigo-400",
    tag: "Industry Solution",
    stats: [
      { value: "60%", label: "Risk Reduction" },
      { value: "35%", label: "Budget Savings" },
      { value: "5×", label: "Faster Compliance" },
    ],
  },
  food: {
    gradient: "from-slate-900 via-green-900 to-emerald-900",
    accent: "from-green-400 to-emerald-400",
    tag: "Industry Solution",
    stats: [
      { value: "25%", label: "Cost Savings" },
      { value: "2.5h", label: "Saved Daily" },
      { value: "40%", label: "Inventory Accuracy" },
    ],
  },
};

const productConfig = {
  "ar-automation": {
    gradient: "from-slate-900 via-cyan-900 to-blue-900",
    accent: "from-cyan-400 to-blue-400",
    tag: "Product",
    subtitle: "Automate your accounts receivable and accelerate cash flow with AI-powered invoice and payment management.",
  },
  b2b: {
    gradient: "from-slate-900 via-indigo-900 to-blue-900",
    accent: "from-indigo-400 to-blue-400",
    tag: "Product",
    subtitle: "Connect buyers and suppliers in a powerful AI-driven digital marketplace that drives revenue and cuts procurement costs.",
  },
  customer: {
    gradient: "from-slate-900 via-rose-900 to-pink-900",
    accent: "from-rose-400 to-pink-400",
    tag: "Product",
    subtitle: "Manage your entire customer lifecycle — from onboarding to retention — with AI-driven insights and automation.",
  },
  delivery: {
    gradient: "from-slate-900 via-amber-900 to-orange-900",
    accent: "from-amber-400 to-orange-400",
    tag: "Product",
    subtitle: "Optimize every delivery with real-time tracking, AI-powered route planning, and seamless driver coordination.",
  },
  distributor: {
    gradient: "from-slate-900 via-teal-900 to-cyan-900",
    accent: "from-teal-400 to-cyan-400",
    tag: "Product",
    subtitle: "Scale your distribution network effortlessly with automated workflows, real-time order tracking, and AI-powered insights.",
  },
  financing: {
    gradient: "from-slate-900 via-emerald-900 to-green-900",
    accent: "from-emerald-400 to-green-400",
    tag: "Product",
    subtitle: "Embed flexible payment and financing solutions directly into your B2B operations to boost purchasing power and sales.",
  },
  inventory: {
    gradient: "from-slate-900 via-blue-900 to-indigo-900",
    accent: "from-blue-400 to-indigo-400",
    tag: "Product",
    subtitle: "Maintain perfect stock levels with AI-driven demand forecasting, real-time inventory tracking, and automated replenishment.",
  },
  salesforce: {
    gradient: "from-slate-900 via-violet-900 to-purple-900",
    accent: "from-violet-400 to-purple-400",
    tag: "Product",
    subtitle: "Empower your sales team with AI automation, voice ordering, competitive pricing insights, and real-time order management.",
  },
  supplier: {
    gradient: "from-slate-900 via-sky-900 to-blue-900",
    accent: "from-sky-400 to-blue-400",
    tag: "Product",
    subtitle: "Build stronger supplier relationships, streamline collaboration, and drive efficiency with AI-powered supplier management.",
  },
  warehouse: {
    gradient: "from-slate-900 via-slate-800 to-zinc-900",
    accent: "from-slate-400 to-zinc-400",
    tag: "Product",
    subtitle: "Optimize warehouse operations with intelligent inventory control, AI demand forecasting, and automated stock management.",
  },
};

export default function Banner({ headline, from, bannerImage, subtitle, alreadyHaveTopPadding }) {
  const config =
    industryConfig[from] || productConfig[from] || {
      gradient: "from-slate-900 via-blue-900 to-indigo-900",
      accent: "from-blue-400 to-indigo-400",
      tag: "Solution",
      subtitle: subtitle || "",
    };

  const resolvedSubtitle = subtitle || config.subtitle || "";

  return (
    <div
      className={`relative overflow-hidden bg-gradient-to-br ${config.gradient} text-white pt-36 pb-20 lg:pt-44 lg:pb-28`}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white opacity-5 blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-80 h-80 rounded-full bg-white opacity-5 blur-3xl" />
      </div>

      <div className="container relative z-10 text-center">
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="inline-block mb-4"
        >
          <span
            className={`text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full bg-white bg-opacity-10 border border-white border-opacity-20 text-white`}
          >
            {config.tag}
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className={`font-extrabold text-white leading-tight mb-4 text-[32px] lg:text-[52px]`}
        >
          <span className={`bg-gradient-to-r ${config.accent} bg-clip-text text-transparent`}>
            {headline}
          </span>
        </motion.h1>

        {/* Subtitle */}
        {resolvedSubtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-white text-opacity-80 text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed mb-8"
          >
            {resolvedSubtitle}
          </motion.p>
        )}

        {/* Stats bar */}
        {config.stats && config.stats.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex justify-center gap-8 lg:gap-16 mt-8 flex-wrap"
          >
            {config.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className={`text-3xl lg:text-4xl font-extrabold bg-gradient-to-r ${config.accent} bg-clip-text text-transparent`}>
                  {stat.value}
                </div>
                <div className="text-xs lg:text-sm text-white text-opacity-70 mt-1 font-medium uppercase tracking-wide">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
